// src/services/activityService.js
import { dataService } from './dataService';
import { tenantDataService } from './tenantDataService';

const COLLECTION_NAME = 'activities';

/**
 * Activity types for consistent logging
 */
export const ACTIVITY_TYPES = {
  APPOINTMENT_CREATED: 'appointment_created',
  APPOINTMENT_UPDATED: 'appointment_updated',
  APPOINTMENT_CANCELLED: 'appointment_cancelled',
  APPOINTMENT_COMPLETED: 'appointment_completed',
  APPOINTMENT_DNA: 'appointment_dna',
  CLIENT_CREATED: 'client_created',
  CLIENT_UPDATED: 'client_updated',
  SOAP_NOTE_CREATED: 'soap_note_created',
  SOAP_NOTE_UPDATED: 'soap_note_updated',
  INVOICE_CREATED: 'invoice_created',
  INVOICE_PAID: 'invoice_paid',
  INVOICE_SENT: 'invoice_sent',
  ROOM_CREATED: 'room_created',
  ROOM_UPDATED: 'room_updated',
  WAITLIST_ADDED: 'waitlist_added',
  WAITLIST_UPDATED: 'waitlist_updated',
  WAITLIST_CONVERTED: 'waitlist_converted',
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  SYSTEM_BACKUP: 'system_backup',
  SYSTEM_ERROR: 'system_error'
};

/**
 * Log an activity
 * @param {string} type - Activity type from ACTIVITY_TYPES
 * @param {Object} data - Activity data
 * @param {string} userId - User who performed the action
 * @param {string} userRole - User role
 * @returns {Promise<Object>}
 */
export async function logActivity(type, data, userId = null, userRole = null) {
  try {
    const activity = {
      id: Date.now().toString(),
      type,
      data: data || {},
      userId,
      userRole,
      timestamp: new Date().toISOString(),
      date: new Date().toISOString().split('T')[0], // For easy date filtering
      message: generateActivityMessage(type, data),
      icon: getActivityIcon(type),
      severity: getActivitySeverity(type)
    };

    await tenantDataService.create(COLLECTION_NAME, activity);
    
    // Emit custom event for real-time updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('activityLogged', { 
        detail: activity 
      }));
    }

    return activity;
  } catch (error) {
    console.error('Error logging activity:', error);
    throw error;
  }
}

/**
 * Get recent activities
 * @param {number} limit - Number of activities to return
 * @param {string} type - Filter by activity type
 * @returns {Promise<Array>}
 */
export async function getRecentActivities(limit = 10, type = null) {
  try {
    const activities = await tenantDataService.getAll(COLLECTION_NAME);
    
    let filtered = activities;
    if (type) {
      filtered = activities.filter(activity => activity.type === type);
    }

    // Sort by timestamp (newest first) and limit
    return filtered
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting recent activities:', error);
    return [];
  }
}

/**
 * Get activities for a specific date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>}
 */
export async function getActivitiesByDateRange(startDate, endDate) {
  try {
    const activities = await tenantDataService.getAll(COLLECTION_NAME);
    
    return activities.filter(activity => {
      const activityDate = activity.date || activity.timestamp.split('T')[0];
      return activityDate >= startDate && activityDate <= endDate;
    }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  } catch (error) {
    console.error('Error getting activities by date range:', error);
    return [];
  }
}

/**
 * Get activities for a specific user
 * @param {string} userId - User ID
 * @param {number} limit - Number of activities to return
 * @returns {Promise<Array>}
 */
export async function getUserActivities(userId, limit = 50) {
  try {
    const activities = await tenantDataService.getAll(COLLECTION_NAME);
    
    return activities
      .filter(activity => activity.userId === userId)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting user activities:', error);
    return [];
  }
}

/**
 * Generate human-readable message for activity
 * @param {string} type - Activity type
 * @param {Object} data - Activity data
 * @returns {string}
 */
function generateActivityMessage(type, data) {
  switch (type) {
    case ACTIVITY_TYPES.APPOINTMENT_CREATED:
      return `New appointment scheduled with ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.APPOINTMENT_UPDATED:
      return `Appointment updated for ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.APPOINTMENT_CANCELLED:
      return `Appointment cancelled for ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.APPOINTMENT_COMPLETED:
      return `Appointment completed for ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.APPOINTMENT_DNA:
      return `${data.clientName || 'Client'} did not attend appointment`;
    case ACTIVITY_TYPES.CLIENT_CREATED:
      return `New client registered: ${data.clientName || 'Unknown'}`;
    case ACTIVITY_TYPES.CLIENT_UPDATED:
      return `Client profile updated: ${data.clientName || 'Unknown'}`;
    case ACTIVITY_TYPES.SOAP_NOTE_CREATED:
      return `Clinical note completed for ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.SOAP_NOTE_UPDATED:
      return `Clinical note updated for ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.INVOICE_CREATED:
      return `Invoice created for ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.INVOICE_PAID:
      return `Payment received from ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.INVOICE_SENT:
      return `Invoice sent to ${data.clientName || 'client'}`;
    case ACTIVITY_TYPES.ROOM_CREATED:
      return `New room added: ${data.roomName || 'Room'}`;
    case ACTIVITY_TYPES.ROOM_UPDATED:
      return `Room updated: ${data.roomName || 'Room'}`;
    case ACTIVITY_TYPES.WAITLIST_ADDED:
      return `${data.clientName || 'Client'} added to waitlist`;
    case ACTIVITY_TYPES.WAITLIST_CONVERTED:
      return `${data.clientName || 'Client'} converted from waitlist to appointment`;
    case ACTIVITY_TYPES.USER_LOGIN:
      return `${data.userName || 'User'} logged in`;
    case ACTIVITY_TYPES.USER_LOGOUT:
      return `${data.userName || 'User'} logged out`;
    case ACTIVITY_TYPES.SYSTEM_BACKUP:
      return 'System backup completed';
    case ACTIVITY_TYPES.SYSTEM_ERROR:
      return `System error: ${data.error || 'Unknown error'}`;
    default:
      return `Activity: ${type}`;
  }
}

/**
 * Get icon for activity type
 * @param {string} type - Activity type
 * @returns {string}
 */
function getActivityIcon(type) {
  switch (type) {
    case ACTIVITY_TYPES.APPOINTMENT_CREATED:
    case ACTIVITY_TYPES.APPOINTMENT_UPDATED:
      return 'FiCalendar';
    case ACTIVITY_TYPES.APPOINTMENT_CANCELLED:
    case ACTIVITY_TYPES.APPOINTMENT_DNA:
      return 'FiX';
    case ACTIVITY_TYPES.APPOINTMENT_COMPLETED:
      return 'FiCheckCircle';
    case ACTIVITY_TYPES.CLIENT_CREATED:
    case ACTIVITY_TYPES.CLIENT_UPDATED:
      return 'FiUser';
    case ACTIVITY_TYPES.SOAP_NOTE_CREATED:
    case ACTIVITY_TYPES.SOAP_NOTE_UPDATED:
      return 'FiFileText';
    case ACTIVITY_TYPES.INVOICE_CREATED:
    case ACTIVITY_TYPES.INVOICE_SENT:
      return 'FiDollarSign';
    case ACTIVITY_TYPES.INVOICE_PAID:
      return 'FiCheckCircle';
    case ACTIVITY_TYPES.ROOM_CREATED:
    case ACTIVITY_TYPES.ROOM_UPDATED:
      return 'FiGrid';
    case ACTIVITY_TYPES.WAITLIST_ADDED:
    case ACTIVITY_TYPES.WAITLIST_CONVERTED:
      return 'FiClock';
    case ACTIVITY_TYPES.USER_LOGIN:
    case ACTIVITY_TYPES.USER_LOGOUT:
      return 'FiUser';
    case ACTIVITY_TYPES.SYSTEM_BACKUP:
      return 'FiDatabase';
    case ACTIVITY_TYPES.SYSTEM_ERROR:
      return 'FiAlertTriangle';
    default:
      return 'FiActivity';
  }
}

/**
 * Get severity level for activity
 * @param {string} type - Activity type
 * @returns {string}
 */
function getActivitySeverity(type) {
  switch (type) {
    case ACTIVITY_TYPES.SYSTEM_ERROR:
      return 'error';
    case ACTIVITY_TYPES.APPOINTMENT_CANCELLED:
    case ACTIVITY_TYPES.APPOINTMENT_DNA:
      return 'warning';
    case ACTIVITY_TYPES.APPOINTMENT_COMPLETED:
    case ACTIVITY_TYPES.INVOICE_PAID:
    case ACTIVITY_TYPES.SOAP_NOTE_CREATED:
      return 'success';
    default:
      return 'info';
  }
}

/**
 * Get activity statistics for dashboard
 * @param {string} period - Period to analyze ('today', 'week', 'month')
 * @returns {Promise<Object>}
 */
export async function getActivityStats(period = 'today') {
  try {
    const now = new Date();
    let startDate;

    switch (period) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    const activities = await tenantDataService.getAll(COLLECTION_NAME);
    const periodActivities = activities.filter(activity => 
      new Date(activity.timestamp) >= startDate
    );

    const stats = {
      total: periodActivities.length,
      byType: {},
      bySeverity: {
        info: 0,
        success: 0,
        warning: 0,
        error: 0
      },
      hourlyDistribution: new Array(24).fill(0)
    };

    periodActivities.forEach(activity => {
      // Count by type
      stats.byType[activity.type] = (stats.byType[activity.type] || 0) + 1;
      
      // Count by severity
      stats.bySeverity[activity.severity] = (stats.bySeverity[activity.severity] || 0) + 1;
      
      // Hourly distribution
      const hour = new Date(activity.timestamp).getHours();
      stats.hourlyDistribution[hour]++;
    });

    return stats;
  } catch (error) {
    console.error('Error getting activity stats:', error);
    return {
      total: 0,
      byType: {},
      bySeverity: { info: 0, success: 0, warning: 0, error: 0 },
      hourlyDistribution: new Array(24).fill(0)
    };
  }
}

/**
 * Clean up old activities (keep only last 90 days)
 * @returns {Promise<number>} Number of activities deleted
 */
export async function cleanupOldActivities() {
  try {
    const activities = await tenantDataService.getAll(COLLECTION_NAME);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 90);

    const toDelete = activities.filter(activity => 
      new Date(activity.timestamp) < cutoffDate
    );

    for (const activity of toDelete) {
      await tenantDataService.delete(COLLECTION_NAME, activity.id);
    }

    return toDelete.length;
  } catch (error) {
    console.error('Error cleaning up old activities:', error);
    return 0;
  }
}
